import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Toggle } from '../ui/Toggle';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { ArrowLeftIcon, SaveIcon, AlertTriangleIcon, PlusIcon, XIcon } from 'lucide-react';
const sections = [{
  id: 'methods',
  label: 'Methods'
}, {
  id: 'tools',
  label: 'Tools'
}, {
  id: 'resources',
  label: 'Resources'
}, {
  id: 'filesystem',
  label: 'Filesystem'
}, {
  id: 'network',
  label: 'Network Egress'
}, {
  id: 'limits',
  label: 'Limits'
}, {
  id: 'redaction',
  label: 'Redaction'
}];
const availableMethods = [{
  id: 'tools/list',
  label: 'tools/list',
  description: 'List available tools'
}, {
  id: 'tools/call',
  label: 'tools/call',
  description: 'Execute a tool'
}, {
  id: 'resources/list',
  label: 'resources/list',
  description: 'List available resources'
}, {
  id: 'resources/read',
  label: 'resources/read',
  description: 'Read a resource'
}, {
  id: 'prompts/list',
  label: 'prompts/list',
  description: 'List available prompts'
}, {
  id: 'prompts/get',
  label: 'prompts/get',
  description: 'Get a prompt'
}];
const discoveredTools = [{
  id: 'search',
  name: 'search',
  description: 'Search the database'
}, {
  id: 'create_record',
  name: 'create_record',
  description: 'Create a new record'
}, {
  id: 'update_record',
  name: 'update_record',
  description: 'Update an existing record'
}, {
  id: 'delete_record',
  name: 'delete_record',
  description: 'Delete a record'
}, {
  id: 'export_data',
  name: 'export_data',
  description: 'Export data to file'
}, {
  id: 'send_email',
  name: 'send_email',
  description: 'Send an email notification'
}];
export function PolicyBuilder() {
  const {
    serverId
  } = useParams();
  const [activeSection, setActiveSection] = useState('methods');
  const [allowedMethods, setAllowedMethods] = useState(['tools/list', 'tools/call']);
  const [allowedTools, setAllowedTools] = useState(['search', 'create_record', 'update_record']);
  const [networkEgress, setNetworkEgress] = useState(false);
  const [egressAllowlist, setEgressAllowlist] = useState<string[]>([]);
  const [newEgressDomain, setNewEgressDomain] = useState('');
  const [requestLimit, setRequestLimit] = useState('1');
  const [responseLimit, setResponseLimit] = useState('10');
  const [hasChanges, setHasChanges] = useState(false);
  const toggleMethod = (methodId: string) => {
    setAllowedMethods(prev => prev.includes(methodId) ? prev.filter(m => m !== methodId) : [...prev, methodId]);
    setHasChanges(true);
  };
  const toggleTool = (toolId: string) => {
    setAllowedTools(prev => prev.includes(toolId) ? prev.filter(t => t !== toolId) : [...prev, toolId]);
    setHasChanges(true);
  };
  const addEgressDomain = () => {
    if (newEgressDomain && !egressAllowlist.includes(newEgressDomain)) {
      setEgressAllowlist([...egressAllowlist, newEgressDomain]);
      setNewEgressDomain('');
      setHasChanges(true);
    }
  };
  const removeEgressDomain = (domain: string) => {
    setEgressAllowlist(egressAllowlist.filter(d => d !== domain));
    setHasChanges(true);
  };
  return <div className="flex gap-6" data-id="element-275">
      {/* Left Sidebar */}
      <div className="w-56 flex-shrink-0" data-id="element-276">
        <Link to={`/servers/${serverId}`} className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-6" data-id="element-277">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-278" />
          Back to Server
        </Link>

        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4" data-id="element-279">
          Policy Builder
        </h2>

        <nav className="space-y-1" data-id="element-280">
          {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`
                w-full text-left px-3 py-2 text-sm rounded-md transition-colors
                ${activeSection === section.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}
              `} data-id="element-281">
              {section.label}
            </button>)}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0" data-id="element-282">
        <Card data-id="element-283">
          {activeSection === 'methods' && <>
              <CardHeader title="Allowed Methods" description="Select which MCP methods are permitted" data-id="element-284" />
              <div className="space-y-2" data-id="element-285">
                {availableMethods.map(method => <label key={method.id} className="flex items-center gap-3 p-3 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" data-id="element-286">
                    <input type="checkbox" checked={allowedMethods.includes(method.id)} onChange={() => toggleMethod(method.id)} className="h-4 w-4 text-blue-600 rounded border-slate-300 dark:border-slate-700 focus:ring-blue-500" data-id="element-287" />
                    <div className="flex-1" data-id="element-288">
                      <span className="text-sm font-mono text-slate-900 dark:text-slate-100" data-id="element-289">
                        {method.label}
                      </span>
                      <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-290">
                        {method.description}
                      </p>
                    </div>
                  </label>)}
              </div>
            </>}

          {activeSection === 'tools' && <>
              <CardHeader title="Allowed Tools" description="Select which tools can be executed" data-id="element-291" />
              <div className="space-y-2" data-id="element-292">
                {discoveredTools.map(tool => <label key={tool.id} className="flex items-center gap-3 p-3 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" data-id="element-293">
                    <input type="checkbox" checked={allowedTools.includes(tool.id)} onChange={() => toggleTool(tool.id)} className="h-4 w-4 text-blue-600 rounded border-slate-300 dark:border-slate-700 focus:ring-blue-500" data-id="element-294" />
                    <div className="flex-1" data-id="element-295">
                      <span className="text-sm font-mono text-slate-900 dark:text-slate-100" data-id="element-296">
                        {tool.name}
                      </span>
                      <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-297">
                        {tool.description}
                      </p>
                    </div>
                  </label>)}
              </div>
            </>}

          {activeSection === 'network' && <>
              <CardHeader title="Network Egress" description="Control outbound network access" data-id="element-298" />
              <div className="space-y-4" data-id="element-299">
                <Toggle label="Enable Network Egress" description="Allow the server to make outbound network requests" checked={networkEgress} onChange={checked => {
              setNetworkEgress(checked);
              setHasChanges(true);
            }} danger data-id="element-300" />

                {networkEgress && <>
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 rounded-md" data-id="element-301">
                      <AlertTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" data-id="element-302" />
                      <p className="text-xs text-amber-800 dark:text-amber-300" data-id="element-303">
                        Network egress is a high-risk capability. Only allow
                        specific domains required for your use case.
                      </p>
                    </div>

                    <div data-id="element-304">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-305">
                        Egress Allowlist
                      </label>
                      <div className="flex gap-2 mb-3" data-id="element-306">
                        <Input placeholder="api.example.com" value={newEgressDomain} onChange={e => setNewEgressDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && addEgressDomain()} data-id="element-307" />
                        <Button variant="secondary" onClick={addEgressDomain} data-id="element-308">
                          <PlusIcon className="h-4 w-4" data-id="element-309" />
                        </Button>
                      </div>
                      {egressAllowlist.length > 0 ? <div className="space-y-2" data-id="element-310">
                          {egressAllowlist.map(domain => <div key={domain} className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-md" data-id="element-311">
                              <span className="text-sm font-mono text-slate-700 dark:text-slate-300" data-id="element-312">
                                {domain}
                              </span>
                              <button onClick={() => removeEgressDomain(domain)} className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400" data-id="element-313">
                                <XIcon className="h-4 w-4" data-id="element-314" />
                              </button>
                            </div>)}
                        </div> : <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-315">
                          No domains in allowlist. All egress will be blocked.
                        </p>}
                    </div>
                  </>}
              </div>
            </>}

          {activeSection === 'limits' && <>
              <CardHeader title="Request & Response Limits" description="Set size limits for requests and responses" data-id="element-316" />
              <div className="grid grid-cols-2 gap-4" data-id="element-317">
                <div data-id="element-318">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-319">
                    Request Body Limit
                  </label>
                  <div className="flex items-center gap-2" data-id="element-320">
                    <Input type="number" value={requestLimit} onChange={e => {
                  setRequestLimit(e.target.value);
                  setHasChanges(true);
                }} className="w-24" data-id="element-321" />
                    <span className="text-sm text-slate-500 dark:text-slate-400" data-id="element-322">
                      MB
                    </span>
                  </div>
                </div>
                <div data-id="element-323">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-324">
                    Response Body Limit
                  </label>
                  <div className="flex items-center gap-2" data-id="element-325">
                    <Input type="number" value={responseLimit} onChange={e => {
                  setResponseLimit(e.target.value);
                  setHasChanges(true);
                }} className="w-24" data-id="element-326" />
                    <span className="text-sm text-slate-500 dark:text-slate-400" data-id="element-327">
                      MB
                    </span>
                  </div>
                </div>
              </div>
            </>}

          {(activeSection === 'resources' || activeSection === 'filesystem' || activeSection === 'redaction') && <div className="text-center py-8" data-id="element-328">
              <p className="text-slate-500 dark:text-slate-400" data-id="element-329">
                Configuration for {activeSection} coming soon
              </p>
            </div>}
        </Card>
      </div>

      {/* Right Sidebar - Policy Summary */}
      <div className="w-72 flex-shrink-0 space-y-4" data-id="element-330">
        <Card data-id="element-331">
          <CardHeader title="Policy Summary" data-id="element-332" />
          <div className="space-y-3 text-xs" data-id="element-333">
            <div data-id="element-334">
              <span className="text-slate-500 dark:text-slate-400" data-id="element-335">Mode</span>
              <div className="mt-1" data-id="element-336">
                <Badge variant="neutral" data-id="element-337">Default Deny</Badge>
              </div>
            </div>
            <div data-id="element-338">
              <span className="text-slate-500 dark:text-slate-400" data-id="element-339">
                Allowed Methods
              </span>
              <div className="mt-1 text-slate-900 dark:text-slate-100" data-id="element-340">
                {allowedMethods.length} of {availableMethods.length}
              </div>
            </div>
            <div data-id="element-341">
              <span className="text-slate-500 dark:text-slate-400" data-id="element-342">
                Allowed Tools
              </span>
              <div className="mt-1 text-slate-900 dark:text-slate-100" data-id="element-343">
                {allowedTools.length} of {discoveredTools.length}
              </div>
            </div>
            <div data-id="element-344">
              <span className="text-slate-500 dark:text-slate-400" data-id="element-345">
                Network Egress
              </span>
              <div className="mt-1" data-id="element-346">
                <Badge variant={networkEgress ? 'warning' : 'healthy'} data-id="element-347">
                  {networkEgress ? `${egressAllowlist.length} domains` : 'Disabled'}
                </Badge>
              </div>
            </div>
            <div data-id="element-348">
              <span className="text-slate-500 dark:text-slate-400" data-id="element-349">Limits</span>
              <div className="mt-1 text-slate-900 dark:text-slate-100" data-id="element-350">
                {requestLimit}MB request / {responseLimit}MB response
              </div>
            </div>
          </div>
        </Card>

        {hasChanges && <Card className="border-amber-200 dark:border-amber-900/30 bg-amber-50 dark:bg-amber-900/20" data-id="element-351">
            <div className="flex items-start gap-2" data-id="element-352">
              <AlertTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" data-id="element-353" />
              <div data-id="element-354">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300" data-id="element-355">
                  Unsaved Changes
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5" data-id="element-356">
                  Review and save your policy changes
                </p>
              </div>
            </div>
          </Card>}

        <Button variant="primary" className="w-full" disabled={!hasChanges} data-id="element-357">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-358" />
          Save Policy
        </Button>
      </div>
    </div>;
}